import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { DataHint } from './data-hint';
import { Trip, EventKey, dateTime, duration, metrics } from './model';

const milestones: { key: EventKey; label: string; tone: string }[] = [
  { key: 'arrival', label: 'Chegada real', tone: 'receiving' },
  { key: 'scheduled', label: 'Agendamento', tone: 'scheduled' },
  { key: 'unloaded', label: 'Fim descarregamento', tone: 'receiving' },
  { key: 'docked', label: 'Início carregamento', tone: 'shipping' },
  { key: 'loaded', label: 'Fim expedição', tone: 'shipping' },
  { key: 'released', label: 'Liberação operacional', tone: 'shipping' },
  { key: 'exit', label: 'Saída efetiva', tone: 'exit' },
];

export default function JourneyFlow({ trip }: { trip: Trip }) {
  const track = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    if (!track.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, entry.contentRect.width))
    );
    observer.observe(track.current);
    return () => observer.disconnect();
  }, []);
  const value = metrics(trip);
  const events = milestones
    .flatMap((milestone) => {
      const at = trip.events[milestone.key];
      if (
        !at ||
        !Number.isFinite(Date.parse(at)) ||
        (milestone.key === 'exit' && !value.complete)
      )
        return [];
      return [{ ...milestone, at, timestamp: Date.parse(at) }];
    })
    .sort((first, second) => first.timestamp - second.timestamp);
  if (!events.length) return null;
  const earliest = events[0].timestamp;
  const latest = events[events.length - 1].timestamp;
  const span = Math.max(3600000, latest - earliest);
  const rangeStart = earliest - span * 0.06;
  const rangeEnd = latest + span * 0.06;
  const range = rangeEnd - rangeStart;
  const percent = (timestamp: number) =>
    ((timestamp - rangeStart) / range) * 100;
  const grouped = events.reduce<
    { timestamp: number; at: string; events: typeof events }[]
  >((groups, event) => {
    const previous = groups[groups.length - 1];
    if (previous?.timestamp === event.timestamp) previous.events.push(event);
    else
      groups.push({
        timestamp: event.timestamp,
        at: event.at,
        events: [event],
      });
    return groups;
  }, []);
  const labelWidth = Math.min(156, width);
  const laneEnds: number[] = [];
  const positioned = grouped.map((group) => {
    const left = Math.max(
      0,
      Math.min(
        width - labelWidth,
        (width * percent(group.timestamp)) / 100 - labelWidth / 2
      )
    );
    let lane = laneEnds.findIndex((end) => end + 12 <= left);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = left + labelWidth;
    return { ...group, left: (left / width) * 100, lane };
  });
  const laneHeights = laneEnds.map((_, lane) =>
    Math.max(
      56,
      ...positioned
        .filter((group) => group.lane === lane)
        .map(
          (group) =>
            Math.ceil(
              group.events.map((event) => event.label).join(' / ').length / 20
            ) *
              15 +
            26
        )
    )
  );
  const laneOffsets = laneHeights.map((_, lane) =>
    laneHeights.slice(0, lane).reduce((total, height) => total + height + 12, 0)
  );
  const barTop =
    laneHeights.reduce((total, height) => total + height + 12, 0) + 12;
  const stage = (
    start: string | undefined,
    end: string | undefined,
    label: string,
    tone: string,
    detail: string
  ) => {
    if (!start || !end || Date.parse(end) <= Date.parse(start)) return null;
    const left = percent(Date.parse(start));
    const segmentWidth = percent(Date.parse(end)) - left;
    return (
      <DataHint
        title={label}
        description={`${dateTime(start)} → ${dateTime(end)}. ${detail}`}
      >
        <span
          role="img"
          tabIndex={0}
          aria-label={`${label}: ${dateTime(start)} até ${dateTime(end)}`}
          className={`stay-journey-stage ${tone}`}
          style={{ left: `${left}%`, width: `${segmentWidth}%`, top: barTop }}
        >
          {segmentWidth > 23 ? label : ''}
        </span>
      </DataHint>
    );
  };
  return (
    <div
      ref={track}
      className="stay-journey"
      style={{ height: barTop + 90 }}
      role="group"
      aria-label={`Fluxo cronológico da viagem ${trip.sourceId || trip.id}`}
    >
      <div className="stay-journey-base" style={{ top: barTop }} />
      {!trip.events.unloaded &&
        stage(
          trip.events.arrival || trip.events.scheduled,
          trip.events.loaded || trip.events.released,
          'Intervalo não detalhado',
          'unclassified',
          'Os marcos de início e fim deste intervalo foram recebidos, mas não há horários suficientes para separar recebimento, espera e expedição. As durações REC e EXP são exibidas abaixo, sem alterar a apuração.'
        )}
      {stage(
        trip.events.arrival,
        trip.events.unloaded,
        'Recebimento',
        'receiving',
        `REC informado: ${duration(value.rec)}. O intervalo visual mostra os marcos físicos; a apuração financeira permanece a recebida da origem.`
      )}
      {stage(
        trip.events.unloaded,
        trip.events.docked,
        'Espera',
        'waiting',
        'Intervalo entre o fim da descarga e o início conhecido do carregamento.'
      )}
      {stage(
        trip.events.docked,
        trip.events.loaded,
        'Expedição',
        'shipping',
        `EXP informado: ${duration(value.exp)}.`
      )}
      {!trip.events.docked &&
        stage(
          trip.events.unloaded,
          trip.events.loaded,
          'Até fim expedição',
          'unclassified',
          `O início do carregamento não foi informado; este intervalo não é classificado integralmente como espera ou expedição. EXP apurado: ${duration(value.exp)}.`
        )}
      {value.complete &&
        stage(
          trip.events.loaded || trip.events.released || trip.events.unloaded,
          trip.events.exit,
          'Até a saída',
          'waiting',
          'Intervalo até o registro da saída efetiva.'
        )}
      {positioned.map((group) => {
        const label = group.events.map((event) => event.label).join(' / ');
        const tone = group.events[group.events.length - 1].tone;
        const labelTop = laneOffsets[group.lane];
        const left = percent(group.timestamp);
        return (
          <React.Fragment key={group.timestamp}>
            <div
              className={`stay-journey-label ${tone}`}
              style={{
                left: `${group.left}%`,
                top: labelTop,
                width: labelWidth,
              }}
            >
              <DataHint
                title={label}
                description={`${dateTime(group.at)} · horário recebido da origem.${group.events.length > 1 ? ' Os eventos compartilham o mesmo horário; não foi criada uma sequência artificial entre eles.' : ''}`}
              >
                <span
                  tabIndex={0}
                  role="img"
                  aria-label={`${label} · ${dateTime(group.at)}`}
                >
                  <strong>
                    {group.events.some(
                      (event) => event.key === 'scheduled'
                    ) && <CalendarDays size={15} />}
                    {label}
                  </strong>
                  <time dateTime={group.at}>{dateTime(group.at)}</time>
                </span>
              </DataHint>
            </div>
            <span
              className={`stay-journey-leader ${tone}`}
              style={{
                left: `${left}%`,
                top: labelTop + laneHeights[group.lane],
                height: barTop - labelTop - laneHeights[group.lane] + 16,
              }}
            />
            <span
              className={`stay-journey-dot ${tone}`}
              style={{ left: `${left}%`, top: barTop + 15 }}
            />
          </React.Fragment>
        );
      })}
      <div className="stay-journey-scale" style={{ top: barTop + 37 }}>
        <span>{dateTime(events[0].at)}</span>
        <span>{dateTime(events[events.length - 1].at)}</span>
      </div>
      <div className="stay-journey-totals" style={{ top: barTop + 61 }}>
        <span className="receiving">REC · {duration(value.rec)}</span>
        <span className="shipping">EXP · {duration(value.exp)}</span>
      </div>
    </div>
  );
}
